import { makeAutoObservable, runInAction } from 'mobx'

type IRcsMediaConstraints = {
  accept: string[]
  maxSizeBytes: number
  maxSizeLabel: string
  width?: number
  height?: number
  uploadText: string
}

export const RCS_MEDIA: Record<'logo' | 'banner' | 'optInImage' | 'video', IRcsMediaConstraints> = {
  logo: {
    accept: ['jpg', 'jpeg', 'png'],
    maxSizeBytes: 50 * 1024,
    maxSizeLabel: '50KB',
    width: 224,
    height: 224,
    uploadText: 'jpg, png · 224×224 px · max 50KB',
  },
  banner: {
    accept: ['jpg', 'jpeg', 'png'],
    maxSizeBytes: 200 * 1024,
    maxSizeLabel: '200KB',
    width: 1440,
    height: 448,
    uploadText: 'jpg, png · 1440×448 px · max 200KB',
  },
  optInImage: {
    accept: ['jpg', 'jpeg', 'png'],
    maxSizeBytes: 10 * 1024 * 1024,
    maxSizeLabel: '10 MB',
    uploadText: 'Acceptable file types: jpg, png up to 10 MB',
  },
  video: {
    accept: ['mp4', 'mov'],
    maxSizeBytes: 100 * 1024 * 1024,
    maxSizeLabel: '100 MB',
    uploadText: 'Acceptable file types: mp4, mov up to 100 MB',
  },
}

const readImageSize = (file: File): Promise<{ width: number; height: number } | null> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      resolve({ width: image.naturalWidth, height: image.naturalHeight })
      URL.revokeObjectURL(url)
    }
    image.onerror = () => {
      resolve(null)
      URL.revokeObjectURL(url)
    }
    image.src = url
  })

export class RcsMediaField {
  url: string | null = null
  file: File | null = null
  uploadedUrl: string | null = null
  removed = false
  error: string | null = null

  constructor(private _constraints?: IRcsMediaConstraints) {
    makeAutoObservable(this)
  }

  sync = (url: string | null) => {
    this.url = url
    this.file = null
    this.uploadedUrl = null
    this.removed = false
    this.error = null
  }

  setUploaded = (url: string) => {
    this.uploadedUrl = url
    this.file = null
    this.error = null
  }

  get isDirty() {
    return this.file !== null || this.removed || this.uploadedUrl !== null
  }

  get editUrl(): string | null {
    if (this.removed) return null
    if (this.uploadedUrl) return this.uploadedUrl
    if (this.file) return null
    return this.url
  }

  get hasEditMedia() {
    return this.file !== null || this.editUrl !== null
  }

  addFile = async (file: File) => {
    const error = await this._validate(file)
    runInAction(() => {
      if (error) {
        this.error = error
        return
      }
      this.error = null
      this.file = file
      this.uploadedUrl = null
      this.removed = false
    })
  }

  remove = () => {
    this.file = null
    this.uploadedUrl = null
    this.removed = true
    this.error = null
  }

  setError = (error: string | null) => {
    this.error = error
  }

  reset = () => {
    this.file = null
    this.uploadedUrl = null
    this.removed = false
    this.error = null
  }

  private _validate = async (file: File): Promise<string | null> => {
    if (!this._constraints) return null
    const { accept, maxSizeBytes, maxSizeLabel, width, height } = this._constraints

    const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
    if (!accept.includes(extension)) {
      return `Acceptable file types: ${accept.join(', ')}`
    }

    if (file.size > maxSizeBytes) {
      return `The file is too large. It must not exceed ${maxSizeLabel}`
    }

    if (width && height) {
      const size = await readImageSize(file)
      if (!size) return 'Unable to read the image'
      if (size.width !== width || size.height !== height) {
        return `The image must be ${width}×${height} px`
      }
    }

    return null
  }
}
